import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CardDesign {
  theme?: string; background?: string; background2?: string; accent?: string; text?: string;
  pattern?: 'none' | 'waves' | 'dots' | 'stripes' | 'confetti' | 'grid'; headline?: string;
  show_logo?: boolean; show_amount?: boolean; corner?: 'rounded' | 'square';
}

export interface StoreGiftDesign {
  store_id: number;
  store_name: string;
  logo: string | null;
  /** The store saved a default card design in Gift Cards → Settings. */
  design_set: boolean;
  design: CardDesign;
  /** Designs saved for individual programs, by offer id. */
  programs: Record<string, CardDesign>;
  default_message: string;
  preset_amounts: number[];
  min_amount: number;
  max_amount: number;
  allow_custom_amount: boolean;
}

/**
 * The card faces a store designed in the business console
 * (GET /backend/v2/gift-cards/public/<store id or slug>/), fetched once per store.
 * Any failure means "no design", so the page keeps the photo it showed before.
 */
@Injectable({ providedIn: 'root' })
export class GiftDesignService {
  private cache = new Map<string, Observable<StoreGiftDesign | null>>();

  constructor(private http: HttpClient) {}

  forStore(storeRef: string | number | null | undefined): Observable<StoreGiftDesign | null> {
    if (storeRef === null || storeRef === undefined || storeRef === '') { return of(null); }
    const key = String(storeRef);
    if (!this.cache.has(key)) {
      this.cache.set(key, this.http.get<StoreGiftDesign>(
        `${environment.orderApiBackend}/backend/v2/gift-cards/public/${encodeURIComponent(key)}/`).pipe(
        timeout(5000),
        map(d => d && typeof d === 'object' ? { ...d, programs: d.programs || {}, design: d.design || {} } : null),
        catchError(() => of(null)),
        shareReplay(1)));
    }
    return this.cache.get(key)!;
  }
}

/**
 * The face to draw for a card, or null to keep the old photo look.
 * A program's own design wins; otherwise the store default, but only if the
 * merchant saved one. Store credit and cards without a program use the default.
 */
export function designFor(d: StoreGiftDesign | null | undefined, offerId?: number | string | null): CardDesign | null {
  if (!d) { return null; }
  const own = offerId !== null && offerId !== undefined ? d.programs[String(offerId)] : undefined;
  if (own) { return { ...d.design, ...own }; }
  return d.design_set ? d.design : null;
}
